import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, Menu, X, Rocket, Quote, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  CASE_STUDY_BRAND,
  getCaseStudyBySlug,
  getRelatedCaseStudies,
  industryCaseStudies,
} from '../data/industryCaseStudies';
import '../styles/landing-clean.css';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

function AnimatedSection({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className}>
      {children}
    </motion.div>
  );
}

function FloatingOrbs() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.div
        className="absolute w-[700px] h-[700px] rounded-full bg-white/30 blur-[120px]"
        style={{ top: '-18%', left: '-12%' }}
        animate={{ x: [0, 40, 0], y: [0, 24, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute w-[480px] h-[480px] rounded-full bg-[#ef9f00]/25 blur-[100px]"
        style={{ top: '30%', right: '-10%' }}
        animate={{ x: [0, -30, 0], y: [0, 36, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
      />
      <motion.div
        className="absolute w-[360px] h-[360px] rounded-full bg-white/25 blur-[90px]"
        style={{ bottom: '8%', left: '40%' }}
        animate={{ x: [0, 20, 0], y: [0, -24, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 8 }}
      />
    </div>
  );
}

function MarketingNav({ brand = CASE_STUDY_BRAND }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/products', label: 'Products' },
    { to: '/case-studies', label: 'Case Studies' },
    { to: '/#pricing-section', label: 'Pricing' },
  ];

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#fffaf7]/90 backdrop-blur-xl border-b border-stone-200/80 shadow-sm'
          : 'bg-transparent'
      }`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shadow-[0_4px_0_0_rgba(22,19,13,0.9)]"
            style={{ background: '#16130d' }}
          >
            <Rocket className="w-5 h-5 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
          </div>
          <span className="text-xl font-heading font-extrabold tracking-tight text-[#16130d]">{brand}</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="text-sm font-bold text-[#16130d] hover:text-black transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/auth')}
            className="!text-[#16130d] font-bold hover:!text-black hover:bg-stone-100 text-sm"
          >
            Login
          </Button>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Button
              onClick={() => navigate('/auth')}
              className="!bg-[#FF6B4A] !text-white hover:!bg-[#f25536] rounded-full px-6 py-2 text-sm font-bold shadow-[0_8px_24px_rgba(255,107,74,0.28)]"
            >
              Get started free
            </Button>
          </motion.div>
        </div>
        <button
          className="md:hidden text-stone-500 hover:text-stone-900"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-[#fffaf7] border-t border-stone-200 px-6 py-4 flex flex-col gap-4"
          >
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-bold text-[#16130d] hover:text-black"
              >
                {l.label}
              </Link>
            ))}
            <Button
              onClick={() => navigate('/auth')}
              className="!bg-[#FF6B4A] !text-white rounded-full font-bold w-full"
            >
              Get started free
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

function MarketingFooter({ brand = CASE_STUDY_BRAND }) {
  return (
    <footer className="border-t border-stone-200 bg-white py-12 px-6 md:px-12">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#16130d' }}>
            <Rocket className="w-4 h-4 text-[#fbbf0f] -rotate-45" strokeWidth={2.4} />
          </div>
          <span className="font-heading font-bold text-[#16130d]">{brand}</span>
        </Link>
        <div className="flex flex-wrap items-center gap-6 text-sm text-stone-500">
          <Link to="/products" className="hover:text-stone-800 transition-colors">Products</Link>
          <Link to="/case-studies" className="hover:text-stone-800 transition-colors">Case Studies</Link>
          <a href="/subscription-plans" className="hover:text-stone-800 transition-colors">Pricing</a>
          <a href="/privacy" className="hover:text-stone-800 transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-stone-800 transition-colors">Terms</a>
        </div>
        <p className="text-stone-400 text-sm">© 2026 {brand}. All rights reserved.</p>
      </div>
    </footer>
  );
}

function HeroVisual({ study }) {
  return (
    <motion.div
      className="relative w-full max-w-xl mx-auto lg:mx-0"
      initial={{ opacity: 0, scale: 0.96, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="absolute -inset-6 rounded-[2.5rem] bg-[#16130d]/10 blur-2xl" />
      <div className="relative overflow-hidden rounded-[2rem] border-2 border-[#16130d] bg-white shadow-[0_12px_0_0_#16130d]">
        <div className="h-12 border-b-2 border-[#16130d] bg-[#fdf3ce] flex items-center px-4 gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#16130d]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#16130d]/40" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#16130d]/20" />
          <span className="ml-3 text-xs font-bold text-[#16130d]/70 tracking-wide">
            {study.industry.toLowerCase()}.snagcart.app
          </span>
        </div>
        <div className="p-6 md:p-8 bg-[#fffefb]">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8a8072] mb-3">
            {study.eyebrow}
          </p>
          <p className="font-heading text-2xl md:text-3xl font-extrabold text-[#16130d] leading-tight mb-4">
            {study.metricHighlight.value}
            <span className="block text-base md:text-lg font-bold text-[#3a3327] mt-1">
              {study.metricHighlight.label}
            </span>
          </p>
          <div className="grid grid-cols-2 gap-3 mt-6">
            {study.results.slice(0, 4).map((r, i) => (
              <motion.div
                key={r.label}
                className="rounded-2xl border border-[#16130d]/10 bg-[#fdf3ce]/60 px-3 py-3"
                animate={{ y: [0, i % 2 === 0 ? -4 : 4, 0] }}
                transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut' }}
              >
                <p className="font-heading font-extrabold text-[#16130d] text-lg">{r.value}</p>
                <p className="text-[11px] font-semibold text-[#5c5245] leading-snug">{r.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const IndustryCaseStudy = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const study = getCaseStudyBySlug(slug);
  const related = getRelatedCaseStudies(slug, 3);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!study) {
    return (
      <div className="landing-clean min-h-screen flex flex-col">
        <MarketingNav />
        <div className="flex-1 flex flex-col items-center justify-center px-6 pt-28 pb-20 text-center">
          <h1 className="font-heading text-4xl font-extrabold text-[#16130d] mb-4">Case study not found</h1>
          <p className="text-[#5c5245] mb-8">That industry story isn’t available yet.</p>
          <Button onClick={() => navigate('/case-studies')} className="!bg-[#FF6B4A] !text-white rounded-full font-bold">
            Browse case studies
          </Button>
        </div>
        <MarketingFooter />
      </div>
    );
  }

  return (
    <div className="landing-clean min-h-screen overflow-x-hidden" data-testid="industry-case-study">
      <MarketingNav />

      {/* Hero — full-bleed yellow, brand-first, one composition */}
      <section
        ref={heroRef}
        className="relative min-h-[92vh] flex items-center pt-28 pb-16 px-6 md:px-12 overflow-hidden bg-[#fbbf0f]"
      >
        <FloatingOrbs />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(rgba(28,25,23,0.04) 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        />
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center"
        >
          <div>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
              className="font-heading text-sm md:text-base font-extrabold tracking-tight text-[#16130d] mb-4"
            >
              {CASE_STUDY_BRAND}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05] text-[#16130d] mb-5"
              data-testid="case-study-title"
            >
              {study.headline}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
              className="text-lg md:text-xl text-[#3a3327] font-medium max-w-xl mb-8 leading-relaxed"
            >
              {study.summary}
            </motion.p>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
              className="flex flex-wrap items-center gap-4"
            >
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button
                  onClick={() => navigate('/auth')}
                  className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-8 py-3 text-base font-bold shadow-[0_5px_0_0_#16130d] hover:shadow-[0_3px_0_0_#16130d] hover:translate-y-[2px] transition-all"
                >
                  Start free with {CASE_STUDY_BRAND}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </motion.div>
              <Link
                to="/case-studies"
                className="text-sm font-bold text-[#16130d] underline underline-offset-4 decoration-[#16130d]/30 hover:decoration-[#16130d]"
              >
                All industries
              </Link>
            </motion.div>
          </div>
          <HeroVisual study={study} />
        </motion.div>
      </section>

      {/* Challenge */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
          <AnimatedSection>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.18em] text-[#8a8072] mb-3">
              The challenge
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl md:text-4xl font-extrabold text-[#16130d] mb-5">
              {study.challenge.title}
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-[#3a3327] font-medium leading-relaxed">
              {study.challenge.body}
            </motion.p>
          </AnimatedSection>
          <AnimatedSection className="space-y-3">
            {study.challenge.pains.map((pain, i) => (
              <motion.div
                key={pain}
                variants={fadeUp}
                custom={i}
                whileHover={{ x: 6 }}
                className="flex gap-3 items-start rounded-2xl border border-[#16130d]/10 bg-[#fdf6df]/70 px-5 py-4"
              >
                <span className="mt-0.5 w-6 h-6 rounded-lg bg-[#16130d] text-[#fbbf0f] flex items-center justify-center text-xs font-extrabold shrink-0">
                  {i + 1}
                </span>
                <p className="font-semibold text-[#16130d] leading-snug">{pain}</p>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* Solution */}
      <section className="py-24 px-6 md:px-12 bg-[#fdf6df]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="max-w-3xl mb-14">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.18em] text-[#8a8072] mb-3">
              The {CASE_STUDY_BRAND} approach
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl md:text-4xl font-extrabold text-[#16130d] mb-5">
              {study.solution.title}
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-lg text-[#3a3327] font-medium leading-relaxed">
              {study.solution.body}
            </motion.p>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 gap-5">
            {study.solution.pillars.map((p, i) => (
              <AnimatedSection key={p.title}>
                <motion.div
                  variants={fadeUp}
                  custom={i}
                  whileHover={{ y: -6 }}
                  className="h-full rounded-3xl border-2 border-[#16130d]/10 bg-white p-7 shadow-[0_8px_0_0_rgba(22,19,13,0.06)]"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#fbbf0f] border-2 border-[#16130d] flex items-center justify-center mb-4 font-heading font-extrabold text-[#16130d]">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-heading text-xl font-extrabold text-[#16130d] mb-2">{p.title}</h3>
                  <p className="text-[#5c5245] font-medium leading-relaxed">{p.desc}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-24 px-6 md:px-12 bg-[#16130d]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: '#fbbf0f' }}>
              Results
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl md:text-4xl font-extrabold" style={{ color: '#fffefb' }}>
              Outcomes that show up in the numbers
            </motion.h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {study.results.map((r, i) => (
              <AnimatedSection key={r.label}>
                <motion.div variants={fadeUp} custom={i} className="text-center md:text-left">
                  <p className="font-heading text-4xl md:text-5xl font-extrabold" style={{ color: '#fbbf0f' }}>
                    {r.value}
                  </p>
                  <p className="mt-2 text-sm font-semibold" style={{ color: 'rgba(255,254,251,0.72)' }}>{r.label}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Playbook */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="mb-12 max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold uppercase tracking-[0.18em] text-[#8a8072] mb-3">
              Playbook
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl md:text-4xl font-extrabold text-[#16130d]">
              How teams roll this out
            </motion.h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6">
            {study.playbook.map((step, i) => (
              <AnimatedSection key={step.step}>
                <motion.div
                  variants={fadeUp}
                  custom={i}
                  whileHover={{ y: -4 }}
                  className="relative rounded-3xl border-2 border-[#16130d] bg-[#fbbf0f] p-7 shadow-[0_8px_0_0_#16130d]"
                >
                  <p className="font-heading text-4xl font-extrabold text-[#16130d]/25 mb-2">{step.step}</p>
                  <h3 className="font-heading text-xl font-extrabold text-[#16130d] mb-2">{step.title}</h3>
                  <p className="text-[#3a3327] font-medium">{step.desc}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Quote + stack */}
      <section className="py-24 px-6 md:px-12 bg-[#fffefb]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_0.8fr] gap-10">
          <AnimatedSection>
            <motion.blockquote
              variants={fadeUp}
              custom={0}
              className="rounded-[2rem] border-2 border-[#16130d] bg-white p-8 md:p-10 shadow-[0_10px_0_0_#16130d]"
            >
              <Quote className="w-10 h-10 text-[#fbbf0f] mb-5" strokeWidth={2.2} />
              <p className="font-heading text-xl md:text-2xl font-bold text-[#16130d] leading-snug mb-6">
                “{study.story.quote}”
              </p>
              <p className="font-extrabold text-[#16130d]">{study.story.company}</p>
              <p className="text-sm font-semibold text-[#5c5245]">{study.story.role}</p>
              <p className="mt-4 text-sm font-bold text-[#16130d] bg-[#fdf3ce] inline-block px-3 py-1.5 rounded-full">
                {study.story.outcome}
              </p>
            </motion.blockquote>
          </AnimatedSection>
          <AnimatedSection>
            <motion.div variants={fadeUp} custom={1} className="rounded-[2rem] bg-[#16130d] p-8 md:p-10 h-full">
              <p className="text-xs font-bold uppercase tracking-[0.18em] mb-4" style={{ color: '#fbbf0f' }}>Stack used</p>
              <ul className="space-y-3">
                {study.stack.map((item) => (
                  <li key={item} className="flex items-center gap-3 font-semibold" style={{ color: '#fffefb' }}>
                    <span className="w-7 h-7 rounded-lg bg-[#fbbf0f] flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-[#16130d]" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 px-6 md:px-12 bg-white border-t border-stone-200/70">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="flex items-end justify-between gap-4 mb-10">
            <motion.h2 variants={fadeUp} custom={0} className="font-heading text-2xl md:text-3xl font-extrabold text-[#16130d]">
              More industries
            </motion.h2>
            <Link to="/case-studies" className="text-sm font-bold text-[#16130d] inline-flex items-center gap-1 hover:gap-2 transition-all">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r, i) => (
              <AnimatedSection key={r.slug}>
                <motion.div variants={fadeUp} custom={i} whileHover={{ y: -6 }}>
                  <Link
                    to={`/case-studies/${r.slug}`}
                    className="block h-full rounded-3xl border border-[#16130d]/10 bg-[#fdf6df]/80 p-6 hover:border-[#16130d]/30 transition-colors"
                  >
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8a8072] mb-2">{r.eyebrow}</p>
                    <h3 className="font-heading text-xl font-extrabold text-[#16130d] mb-2">{r.industry}</h3>
                    <p className="text-sm text-[#5c5245] font-medium leading-relaxed mb-4 line-clamp-2">{r.summary}</p>
                    <span className="text-sm font-bold text-[#16130d] inline-flex items-center gap-1">
                      Read story <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden bg-[#fbbf0f]">
        <FloatingOrbs />
        <AnimatedSection className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.h2 variants={fadeUp} custom={0} className="font-heading text-3xl md:text-5xl font-extrabold text-[#16130d] mb-4">
            Ready to build this for {study.industry.toLowerCase()}?
          </motion.h2>
          <motion.p variants={fadeUp} custom={1} className="text-[#3a3327] text-lg mb-8 font-medium">
            Start free on {CASE_STUDY_BRAND} — same workspace for sites, marketing, AI, and ops.
          </motion.p>
          <motion.div variants={fadeUp} custom={2}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button
                onClick={() => navigate('/auth')}
                className="!bg-white !text-[#16130d] hover:!bg-white border-2 border-[#16130d] rounded-full px-10 py-4 text-lg font-bold shadow-[0_5px_0_0_#16130d] hover:shadow-[0_3px_0_0_#16130d] hover:translate-y-[2px] transition-all"
              >
                Get started free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* Industry jump chips */}
      <div className="bg-white border-t border-stone-200 px-6 md:px-12 py-6">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-center">
          {industryCaseStudies.map((c) => (
            <Link
              key={c.slug}
              to={`/case-studies/${c.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                c.slug === study.slug
                  ? 'bg-[#16130d] text-[#fbbf0f] border-[#16130d]'
                  : 'bg-[#fdf3ce] text-[#16130d] border-transparent hover:border-[#16130d]/20'
              }`}
            >
              {c.industry}
            </Link>
          ))}
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
};

export default IndustryCaseStudy;
export { MarketingNav, MarketingFooter, FloatingOrbs, fadeUp, AnimatedSection };
